import { SafeError, SafePromise } from '@benzinga/safe-await';
import { ListenableSubscribable } from '@benzinga/subscribable';
import { ScannerRestful } from './restful';
import { Session } from '@benzinga/session';
import { ScannerProtos, CommonProtos } from './scanner_protos';
import { ScannerConfig } from '@benzinga/scanner-config-manager';
import { configToQuery } from './utils';

interface ErrorEvent {
  error?: SafeError | CommonProtos.IErr | Response;
  errorType: 'get_scanner_instruments_error';
  type: 'error';
}

interface ScannerInstrumentsEvent {
  type: 'get_scanner_instruments';
  scannerResponse: ScannerProtos.IQueryResponse;
}

export type ScannerInstrumentsRequestEvent = ErrorEvent | ScannerInstrumentsEvent;
export class ScannerRequest extends ListenableSubscribable<ScannerInstrumentsRequestEvent> {
  private restful: ScannerRestful;
  private session: Session;

  constructor(session: Session) {
    super();
    this.session = session;
    this.restful = new ScannerRestful(session);
  }

  public getScannerInstruments = async (config: ScannerConfig): SafePromise<ScannerProtos.IScannerResult> => {
    const query = configToQuery(config, this.session);
    if (query.err) {
      return query;
    }
    const response = await this.restful.getScannerInstruments(query.ok, config.source);

    if (response.err) {
      const errorBody = response.err.data;
      if (errorBody instanceof Response) {
        const data = await errorBody.arrayBuffer();
        const decodedError = CommonProtos.Err.decode(new Uint8Array(data));

        this.dispatch({
          error: decodedError,
          errorType: 'get_scanner_instruments_error',
          type: 'error',
        });

        return { err: new SafeError(decodedError.message, response.err.type, decodedError) };
      }

      this.dispatch({
        error: response.err,
        errorType: 'get_scanner_instruments_error',
        type: 'error',
      });
      return { err: response.err };
    }

    try {
      const data = await response.ok.arrayBuffer();
      const decodedData = ScannerProtos.ScannerResult.decode(new Uint8Array(data));

      this.dispatch({
        scannerResponse: decodedData,
        type: 'get_scanner_instruments',
      });

      return { ok: decodedData };
    } catch (error) {
      console.error('Protobuf decode error', error);
      return { err: new SafeError('Failed to decode scanner response', 'protobuf_decode_error', error) };
    }
  };

  public getScannerInstrumentsWithQuery = async (
    query: ScannerProtos.IQuery,
    source: 'stocks' | 'crypto' = 'stocks',
  ): SafePromise<ScannerProtos.IScannerResult> => {
    const response = await this.restful.getScannerInstruments(new ScannerProtos.Query(query), source);

    if (response.err) {
      const errorBody = response.err.data;
      if (errorBody instanceof Response) {
        const data = await errorBody.arrayBuffer();
        const decodedError = CommonProtos.Err.decode(new Uint8Array(data));

        this.dispatch({
          error: decodedError,
          errorType: 'get_scanner_instruments_error',
          type: 'error',
        });

        return { err: new SafeError(decodedError.message, response.err.type, decodedError) };
      }

      this.dispatch({
        error: response.err,
        errorType: 'get_scanner_instruments_error',
        type: 'error',
      });
      return { err: response.err };
    }

    try {
      const data = await response.ok.arrayBuffer();
      const decodedData = ScannerProtos.ScannerResult.decode(new Uint8Array(data));

      this.dispatch({
        scannerResponse: decodedData,
        type: 'get_scanner_instruments',
      });

      return { ok: decodedData };
    } catch (error) {
      console.error('Protobuf decode error', error);
      return { err: new SafeError('Failed to decode scanner response', 'protobuf_decode_error', error) };
    }
  };

  public getSectorHeatmap = async () => {
    return this.restful.getSectorHeatmap();
  };
}
